<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12">
                <responsiveButton :setText="true"
                                  setColor="orange"
                                  setDefaultIcon="mdi-refresh"
                                  setSmallIcon="mdi-refresh"
                                  :setClick="initialize"
                >
                    Refresh Data
                </responsiveButton>
                <v-divider class="mx-6"></v-divider>
                <v-data-table
                        :headers="computedHeaders"
                        :items="documentGroups"
                        :loading="loading"
                        loading-text="Loading... Please wait"
                        class="elevation-1"
                        :options.sync="pagination"
                        :server-items-length="totalItems"
                >
                    <template v-slot:header.reports_documents>
                        {{tableColumnReportDocuments}}
                    </template>
                    <template v-slot:item.reports_documents="{item}">
                        <v-chip color="green" class="white-span" text-color="white">
                            {{ item.documentRules ? Object.values(item.documentRules).length : '0' }}
                        </v-chip>
                    </template>
                    <template v-slot:item.apply_to="{item}">
                        {{item.applyTo | upperCaseFirstLetter}}
                    </template>
                    <template v-slot:item.systemCreatedDateTime="{item}">
                        {{item.systemCreatedDateTime | formatDate}}
                    </template>
                    <template v-slot:item.edit="{item}">
                        <v-icon
                                class="mr-2"
                                @click="editDocumentGroup(item)"
                        >
                            {{DOCUMENT_GROUP_ACTIONS.EDIT.icon}}
                        </v-icon>
                    </template>
                    <template v-slot:item.clone="{item}">
                        <v-icon
                                class="mr-2"
                                @click="cloneDocumentGroup(item)"
                        >
                            {{DOCUMENT_GROUP_ACTIONS.CLONE.icon}}
                        </v-icon>
                    </template>
                    <template v-slot:item.remove="{item}">
                        <v-icon
                                class="mx-auto"
                                @click="confirmDelete(item)"
                        >
                            {{DOCUMENT_GROUP_ACTIONS.DELETE.icon}}
                        </v-icon>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <dialog-alert
                :text="confirmAlertText"
                :alert-type="confirmAlertType"
                :open="openConfirmation"
                @alert-closed="dialogClosed($event)"
        ></dialog-alert>
    </v-container>
</template>

<script>
    import alertEventBus from '@/eventBus/AlertEventBus';
    const responsiveButton = ()=> import( '@/components/common/mobile/Button');
    import {DOCUMENT_GROUP_ACTIONS, DOCUMENT_GROUP_TYPES} from "@/shared/enums/DocumentGroup";
    const DialogAlert = () => import("@/components/common/DialogAlert");
    import { ALERT_TYPES, CONFIRM_DELETE } from "@/shared/enums/AlertTexts";

    export default {
        name: "documentGroupTable",
        components: { responsiveButton, DialogAlert },
        props:["docType"],
        data(){
            return {
                documentGroups: [],
                loading: true,
                documentType: this.docType,
                documentGroupTable: {
                    headers: [
                        {
                            text: 'Name',
                            align: 'left',
                            sortable: true,
                            value: 'name'
                        },
                        {
                            align: 'center',
                            sortable: false,
                            value: 'reports_documents'
                        },
                        {
                            text: 'Applies To',
                            align: 'center',
                            sortable: true,
                            divider: false,
                            value: 'apply_to'
                        },
                        {
                            text: 'Created Date',
                            align: 'center',
                            sortable: true,
                            divider: true,
                            value: 'systemCreatedDateTime'
                        },
                        {
                            text: 'Edit',
                            align: 'center',
                            width: '2%',
                            value: 'edit',
                            sortable: false
                        },
                        {
                            text: 'Clone',
                            align: 'center',
                            width: '2%',
                            value: 'clone',
                            sortable: false
                        },
                        {
                            text: 'Remove',
                            align: 'center',
                            width: '2%',
                            value: 'remove',
                            sortable: false
                        }
                    ]
                },
                confirmAlertType: ALERT_TYPES.CONFIRM,
                confirmAlertText: CONFIRM_DELETE,
                openConfirmation: false,
                documentGroupToDelete: null,
                pagination: {},
                pageKeyCache: new Map(),
                totalItems: 0
            };
        },
        computed:{
            computedHeaders(){
                return this.documentGroupTable.headers.filter((item) => {

                    return this.isDocument || item.value !== 'apply_to'
                });
            },
            isDocument(){
                return this.documentType === DOCUMENT_GROUP_TYPES.DOCUMENT;
            },
            tableColumnReportDocuments(){
                return this.isDocument ? 'Documents' : 'Reports'
            },
            DOCUMENT_GROUP_ACTIONS(){
               return DOCUMENT_GROUP_ACTIONS;
            }
        },
        watch:{
            docType(value){
                this.documentType = value;
            },
            pagination: {
                async handler() {
                    await this.getDocumentGroups();
                },
                deep: true
            },
        },
        methods:{
            initialize() {
                this.getDocumentGroups();
            },
            routeToDocument(action, id){
                return {name: this.isDocument ? 'documentGroupAction' : 'reportGroupAction',params:{action: action, id: id}};
            },
            async getDocumentGroups() {
                this.loading = true;
                let params = this.getPaginationValues();

                if(!params.limit)
                    return;

                params.type = this.documentType;

                try{
                    await this.$store.dispatch('getDocumentGroups',params);
                    this.documentGroups = this.$store.getters.getDocumentGroups({type:this.documentType});
                }
                catch (e) {
                    //set alert
                    this.loading = false;
                    alertEventBus.setAlert({type:'error', msg: e.message});
                    return;
                }

                this.setPaginationValues(this.documentGroups);

                this.loading = false;
            },
            setPaginationValues(list){
                let key = this.$store.getters.getDocumentGroupsPageKey;
                const {page} = this.pagination;

                if(key != null && Object.keys(key).length > 0){
                    this.pageKeyCache.set(page, key);
                    this.totalItems = list.length +1;
                }
                else{
                    this.totalItems = list.length;
                }
            },
            getPaginationValues(){
                //all values of pagination that can be used for backend
                const { page, itemsPerPage } = this.pagination;
                let params = {
                    limit: itemsPerPage
                }

                if(this.pageKeyCache.has(page) && page != 1){
                    params.lastEvaluatedKey = this.pageKeyCache.get(page);
                }
                else {
                    //we have not been there yet, so it needs to be to cases 1: this is brand new search page 0
                    // or it is the next page that we has not seem
                    if(page != 1) {
                        params.lastEvaluatedKey = this.pageKeyCache.get(page-1);
                    }
                }
                return params;
            },
            async cloneDocumentGroup(documentGroup){
                this.$store.commit('setDocumentGroup',{...documentGroup});
                this.$router.push(this.routeToDocument(DOCUMENT_GROUP_ACTIONS.CLONE.text,documentGroup.id));
            },
            async editDocumentGroup(documentGroup){
                this.$store.commit('setDocumentGroup',documentGroup);
                this.$router.push(this.routeToDocument(DOCUMENT_GROUP_ACTIONS.EDIT.text,documentGroup.id));
            },
            async deleteDocumentGroup(documentGroup) {
                const index = this.documentGroups.indexOf(documentGroup);

                await this.$store.dispatch("deleteDocumentGroup", {
                    id: documentGroup.id
                });
                let response = this.$store.getters.getDocumentGroup;

                this.documentGroups.splice(index, 1);
                this.documentGroupToDelete = null;
            },
            confirmDelete(item) {
                this.openConfirmation = true;
                this.documentGroupToDelete = item;
            },
            dialogClosed(answer) {
                this.openConfirmation = false;
                if (answer) {
                    this.deleteDocumentGroup(this.documentGroupToDelete);
                }
            }
        },
        async created(){
            this.initialize();
        }
    }
</script>

<style>
    .v-chip.white-span span {
        color: white !important;
    }
</style>